import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Section from 'components/shared/Section';
import Button from 'components/shared/Button';
import Content from 'components/shared/Content';
import SectionHeading from 'components/shared/Heading';
import scrollToSection from 'utils/scrollToSection';
import ContactForm from 'components/Contact/ContactForm';

const CardsWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  ${({ theme }) => theme.mq.lg} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Heading = styled.h2`
  font-size: 2.55rem;
  padding-bottom: 10px;
  ${({ theme }) => theme.mq.xs} {
    font-size: 3rem;
  }
  ${({ theme }) => theme.mq.md} {
    font-size: 3.6rem;
    padding-bottom: 30px;
  }
  ${({ theme }) => theme.mq.lg} {
    font-size: 4.2rem;
  }
  ${({ theme }) => theme.mq.xxl} {
    font-size: 4.8rem;
  }
`;

const Card = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  height: 400px;
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => theme.colors.blue};
  &:nth-child(1) {
    padding: 40px 30px;
  }
  &:nth-child(2) {
    background: ${({ theme }) => theme.colors.blue100};
    padding: 0;
    overflow: hidden;
  }
  ${({ theme }) => theme.mq.s} {
    height: 460px;
  }
  ${({ theme }) => theme.mq.lg} {
    justify-content: stretch;
    height: 580px;
    &:nth-child(1) {
      padding: 60px 30px;
      justify-content: space-between;
    }
    &:nth-child(2) {
      padding: 0;
    }
  }
`;

const Detail = styled.p`
  font-size: 1.8rem;
  line-height: 4rem;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  display: block;
  margin: 0;
  padding: 5px 0;
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const CTASection = styled.div`
  text-align: center;
  margin-bottom: 40px;
  ${({ theme }) => theme.mq.lg} {
    margin-bottom: 60px;
  }
`;

const ContactSection = styled.div`
  text-align: center;
`;

const StyledButton = styled(Button)`
  background: ${({ theme }) => theme.colors.white};
  font-weight: ${({ theme }) => theme.light};
  color: ${({ theme }) => theme.colors.dark};
  padding: 18px;
  margin-top: 30px;
  &:hover {
    background: ${({ theme }) => theme.colors.blue100};
    color: ${({ theme }) => theme.colors.white};
  }
  ${({ theme }) => theme.mq.s} {
    padding: 25px;
    max-width: 280px;
  }
`;

const testTerms = [
  {
    name: 'terms',
    content: `Wyrażam zgodę na przetwarzanie moich danych osobowych w postaci imienia i nazwiska, adresu e-mail, numeru telefonu, informacji dotyczących preferencji zakupowych i innych danych wskazanych w treści wiadomości oraz w zakresie informacji o mojej aktywności na stronie internetowej przez Polisa Centrum Ubezpieczeń Katarzyna Sterzel, z siedzibą w Koszalinie w celu marketingu bezpośredniego produktów i usług.`,
    required: true,
  },
];

const Partners = ({ card1, card2 }) => (
  <Section id="contact" contact>
    <CardsWrapper>
      <Card as="address">
        <CTASection>
          <Heading>{card1.heading}</Heading>
          <StyledButton onClick={() => scrollToSection('#contact-form')}>
            {card1.btnText}
          </StyledButton>
        </CTASection>
        <ContactSection>
          <Heading>{card2.heading}</Heading>
          <Detail as="a" href={`mailto:${card2.email}`}>
            E: {card2.email}
          </Detail>
          <Detail as="a" href={`tel:${card2.phone}`}>
            T: {card2.phone}
          </Detail>
          <Detail dangerouslySetInnerHTML={{ __html: card2.address }} />
        </ContactSection>
      </Card>
      <Card>
        <MapFrame
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2346.845037777884!2d16.172389712500003!3d54.190791000000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4701cd09489621eb%3A0x28123a15bc98567a!2sPolisa%20Centrum%20Ubezpiecze%C5%84%20%7C%20Ubezpieczenia%20%7C%20Leasingi%20%7C%20Kredyty%20%7C%20Koszalin!5e0!3m2!1spl!2spl!4v1761249235539!5m2!1spl!2spl"
          title="Mapa lokalizacji Polisa Centrum Ubezpieczeń"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Card>
    </CardsWrapper>
    <Content>
      <SectionHeading id="contact-form">Formularz kontaktowy</SectionHeading>
      <ContactForm
        terms={testTerms}
        info={`Administratorem danych osobowych jest Polisa Centrum Ubezpieczeń Katarzyna Sterzel z siedzibą przy ulicy Zwycięstwa 181 w Koszalinie. Przysługuje Państwu prawo dostępu do swoich danych osobowych oraz prawo do ich usunięcia, poprawiania, ograniczenia przetwarzania oraz cofnięcia zgody na ich przetwarzanie. Podanie danych osobowych jest dobrowolne, wysyłając formularz wyrażają Państwo zgodę na ich przetwarzanie. schowaj Państwa dane będą przetwarzane w celach marketingowych również z wykorzystaniem zautomatyzowanego profilowania Podstawę prawną do przetwarzania Państwa danych osobowych stanowi art. 6 ust. 1 lit.a RODO czyli zgoda w przypadku jej udzielenia lub art. 6 ust. 1 lit.f RODO (ważny interes administratora marketing bezpośredni). Zakres przetwarzanych danych osobowych to imię, nazwisko, funkcja, telefon, adres e-mail. Dane będą przechowywane do momentu wniesienia przez Państwa sprzeciwu. Kontakt z administratorami danych jest możliwy poprzez email: <a href="mailto:polisa@koszalinubezpieczenia">polisa@koszalinubezpieczenia</a>, tel. <a href="tel:+48 888 219 451">+48 888 219 451</a>, oraz korespondencyjnie pod wskazany adres siedziby.`}
      />
    </Content>
  </Section>
);

Partners.propTypes = {
  card1: PropTypes.objectOf(PropTypes.string).isRequired,
  card2: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default Partners;
